import type { GameEvent, GameState } from "../engine/types";

export class GameAudio {
  private context: AudioContext | null = null;
  private windSource: AudioBufferSourceNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private windGain: GainNode | null = null;
  private master: GainNode | null = null;

  async start(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.46;
      this.master.connect(this.context.destination);
      this.createWind();
    }
    await this.context.resume();
  }

  update(state: GameState): void {
    if (!this.context || !this.windFilter || !this.windGain) {
      return;
    }
    const now = this.context.currentTime;
    const pressureRatio = state.pressure / state.modifiers.pressureCapacity;
    this.windFilter.frequency.setTargetAtTime(540 + pressureRatio * 520, now, 0.12);
    this.windGain.gain.setTargetAtTime(state.phase === "running" ? 0.08 + pressureRatio * 0.035 : 0.035, now, 0.2);
  }

  receive(events: GameEvent[]): void {
    for (const event of events) {
      const pan = event.x === undefined ? 0 : Math.max(-1, Math.min(1, (event.x - 500) / 500));
      if (event.kind === "launch") {
        this.tone(260, 640, 0.055, 0.035, "triangle", pan);
        this.noiseBurst(0.045, 0.035, 2400, pan);
      } else if (event.kind === "hit") {
        this.tone(170, 95, 0.1, 0.075, "triangle", pan);
        this.noiseBurst(0.07, 0.045, event.enemyKind === "stoneback" ? 620 : 1500, pan);
      } else if (event.kind === "curved-hit") {
        this.tone(620, 930, 0.16, 0.045, "sine", pan);
      } else if (event.kind === "projectile-lost") {
        this.tone(420, 145, 0.18, 0.028, "sine", pan);
      } else if (event.kind === "defeat-enemy") {
        this.tone(420, 760, 0.16, 0.075, "sine", pan);
        this.noiseBurst(0.11, 0.05, 980, pan);
      } else if (event.kind === "gust") {
        this.noiseBurst(0.22, 0.22, 1200);
        this.tone(110, 240, 0.24, 0.11, "sine");
      } else if (event.kind === "wind-set") {
        this.chord([330, 495, 660], 0.28);
      } else if (event.kind === "hearth-hit") {
        this.tone(95, 52, 0.3, 0.22, "sawtooth");
      } else if (event.kind === "wave-clear") {
        this.chord([330, 440, 660], 0.42);
      } else if (event.kind === "victory") {
        this.chord([330, 495, 660, 825], 0.8);
      } else if (event.kind === "defeat") {
        this.chord([220, 261.6, 311.1], 0.7);
      }
    }
  }

  private createWind(): void {
    if (!this.context || !this.master) {
      return;
    }
    const length = this.context.sampleRate * 2;
    const buffer = this.context.createBuffer(1, length, this.context.sampleRate);
    const channel = buffer.getChannelData(0);
    let previous = 0;
    for (let index = 0; index < length; index += 1) {
      const white = Math.random() * 2 - 1;
      previous = previous * 0.985 + white * 0.015;
      channel[index] = previous * 3.2;
    }
    this.windSource = this.context.createBufferSource();
    this.windSource.buffer = buffer;
    this.windSource.loop = true;
    this.windFilter = this.context.createBiquadFilter();
    this.windFilter.type = "bandpass";
    this.windFilter.frequency.value = 700;
    this.windFilter.Q.value = 0.6;
    this.windGain = this.context.createGain();
    this.windGain.gain.value = 0.05;
    this.windSource.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.master);
    this.windSource.start();
  }

  private tone(
    start: number,
    end: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    pan = 0
  ): void {
    if (!this.context || !this.master) {
      return;
    }
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const panner = this.context.createStereoPanner();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(start, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, end), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    panner.pan.value = pan;
    gain.connect(panner);
    panner.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.01);
  }

  private noiseBurst(duration: number, volume: number, cutoff: number, pan = 0): void {
    if (!this.context || !this.master) {
      return;
    }
    const count = Math.ceil(this.context.sampleRate * duration);
    const buffer = this.context.createBuffer(1, count, this.context.sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < count; index += 1) {
      channel[index] = (Math.random() * 2 - 1) * (1 - index / count);
    }
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    const panner = this.context.createStereoPanner();
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    gain.gain.value = volume;
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gain);
    panner.pan.value = pan;
    gain.connect(panner);
    panner.connect(this.master);
    source.start();
  }

  private chord(frequencies: number[], duration: number): void {
    frequencies.forEach((frequency, index) => {
      window.setTimeout(() => this.tone(frequency, frequency * 1.01, duration, 0.045, "sine"), index * 45);
    });
  }
}
